import React, { useEffect, useState } from 'react';
import { SimpleUser } from '../../services/userLoader';
import { LeaveRequest, LeaveStatus } from '../../types/leave';
import { LeaveBalanceSnapshot } from '../../types/leavePolicy';
import { LeaveBalanceService } from '../../services/leaveBalanceService';
import { leaveDaysFor } from '../../services/leaveDuration';
import { sameIdentity } from '../../services/identity';
import { leaveTypeName } from './pages/leaveShared';
import { expirySummary, expiryTooltip } from './leaveExpiry';
import { usedTooltip } from './leaveUsed';
import './LeavePage.css';

interface EmployeeLeaveProfileDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  employee: SimpleUser | null;
  leaves: LeaveRequest[];
  year: number;
}

export const EmployeeLeaveProfileDrawer: React.FC<EmployeeLeaveProfileDrawerProps> = ({
  isOpen,
  onClose,
  employee,
  leaves,
  year,
}) => {
  const [snapshots, setSnapshots] = useState<LeaveBalanceSnapshot[]>([]);
  const [loadingBalances, setLoadingBalances] = useState(false);
  const [activeTab, setActiveTab] = useState<'ALL' | 'UPCOMING' | 'PENDING' | 'PAST'>('ALL');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');

  useEffect(() => {
    if (!isOpen || !employee?.email) return;
    let active = true;
    const fetchBalances = async () => {
      setLoadingBalances(true);
      try {
        const data = await LeaveBalanceService.getBalances(employee.email, year);
        if (active) setSnapshots(data);
      } catch {
        if (active) setSnapshots([]);
      } finally {
        if (active) setLoadingBalances(false);
      }
    };
    fetchBalances();

    // Without this, a drawer left open while HR grants/adjusts leave elsewhere (another
    // tab, or the same session) keeps showing the balance it had when it was opened —
    // this was the "Employee Profile doesn't update" sync bug.
    window.addEventListener('leave-balance-updated', fetchBalances);
    window.addEventListener('leave-requests-updated', fetchBalances);
    window.addEventListener('storage', fetchBalances);
    return () => {
      active = false;
      window.removeEventListener('leave-balance-updated', fetchBalances);
      window.removeEventListener('leave-requests-updated', fetchBalances);
      window.removeEventListener('storage', fetchBalances);
    };
  }, [isOpen, employee?.email, year]);

  if (!isOpen || !employee) return null;

  const today = new Date().toISOString().split('T')[0];

  /*
   * Matched with sameIdentity, not a lowercased ===.
   *
   * This employee may own two Cognito accounts on two domains, and their leave is stored against
   * whichever address was current when each request was raised. An exact comparison showed HR
   * only the half filed under the address this row happens to carry — so a drawer could report
   * "no leave history" for somebody with a full one.
   */
  const empLeaves = leaves.filter(l => sameIdentity(l.employeeEmail, employee.email));

  const upcomingApproved = empLeaves.filter(l => l.status === LeaveStatus.APPROVED && l.startDate >= today);
  const pendingRequests = empLeaves.filter(l => l.status === LeaveStatus.SUBMITTED);
  const pastHistory = empLeaves.filter(l => l.endDate < today);

  const getFilteredLeaves = () => {
    let list = empLeaves;
    if (activeTab === 'UPCOMING') list = upcomingApproved;
    else if (activeTab === 'PENDING') list = pendingRequests;
    else if (activeTab === 'PAST') list = pastHistory;

    if (statusFilter !== 'ALL') {
      list = list.filter(l => String(l.status).toUpperCase() === statusFilter.toUpperCase());
    }
    return list;
  };

  const filteredList = getFilteredLeaves();

  // Summary statistics
  // The same pool the balance below is measured against — see
  // LeaveBalanceService.totalEntitlement. Bare `granted` here left this drawer showing a
  // total that excluded comp-off and HR grants while the balance beside it included both.
  const totalGranted = snapshots.reduce((acc, s) => acc + LeaveBalanceService.totalEntitlement(s), 0);
  /*
   * totalUsed, not the bare `consumed` field. The system went live mid-year, so leave taken
   * before then was reconciled as a negative HR adjustment rather than as a leave request —
   * summing `consumed` reported 0 days used for an employee whose balance was visibly short
   * of their entitlement, which is the one thing HR opens this drawer to check.
   */
  const totalConsumed = snapshots.reduce((acc, s) => acc + LeaveBalanceService.totalUsed(s), 0);
  const totalPending = snapshots.reduce((acc, s) => acc + s.pending, 0);
  const totalBalance = snapshots.reduce((acc, s) => acc + s.balance, 0);

  return (
    <div className="lc-drawer-backdrop elp-modal-backdrop" onClick={onClose}>
      <div className="lc-drawer-panel elp-drawer-panel elp-modal-panel" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="lc-drawer-header">
          <div>
            <div className="elp-header-eyebrow">Employee Leave Profile</div>
            <h2 className="lc-drawer-title">{employee.fullName || employee.name}</h2>
            <p className="lc-drawer-subtitle">{employee.email} • ID: {employee.employeeId || 'N/A'}</p>
          </div>
          <button type="button" className="lc-drawer-close-pill" onClick={onClose}>
            ✕ Close
          </button>
        </div>

        <div className="lc-drawer-body elp-drawer-body">
          {/* Employee Metadata Grid */}
          <div className="elp-meta-grid">
            <div className="elp-meta-item">
              <span className="elp-meta-label">Department</span>
              <span className="elp-meta-val">{employee.department || '—'}</span>
            </div>
            <div className="elp-meta-item">
              <span className="elp-meta-label">Team / Department</span>
              <span className="elp-meta-val">{employee.team || '—'}</span>
            </div>
            <div className="elp-meta-item">
              <span className="elp-meta-label">Designation</span>
              <span className="elp-meta-val">{employee.designation || '—'}</span>
            </div>
            {/* Manager, because "who approves this person's leave" is the first question asked of
                a leave profile and the only field on HR's list this drawer was missing. Email
                rather than a resolved name: managerEmail is what the employee record stores and
                what every approval path matches on, so showing it keeps the drawer consistent
                with what the rest of the module actually uses. */}
            <div className="elp-meta-item">
              <span className="elp-meta-label">Manager</span>
              <span className="elp-meta-val">{employee.managerEmail || '—'}</span>
            </div>
            <div className="elp-meta-item">
              <span className="elp-meta-label">Employment Type</span>
              <span className="elp-meta-val">{employee.employmentType || '—'}</span>
            </div>
            <div className="elp-meta-item">
              <span className="elp-meta-label">Date of Joining</span>
              <span className="elp-meta-val">{employee.dateOfJoining || '—'}</span>
            </div>
            {/* Only when set: an exit date is the reason accrual stopped, so its absence should
                not occupy a slot on every other employee's profile. */}
            {employee.dateOfExit && (
              <div className="elp-meta-item">
                <span className="elp-meta-label">Date of Exit</span>
                <span className="elp-meta-val">{employee.dateOfExit}</span>
              </div>
            )}
            <div className="elp-meta-item">
              <span className="elp-meta-label">Leave Year</span>
              <span className="elp-meta-val">{year}</span>
            </div>
          </div>

          {/* Aggregate Stats Cards */}
          <div className="elp-stats-row">
            <div className="elp-stat-card">
              <span className="elp-stat-num">{totalGranted}</span>
              <span className="elp-stat-desc">Total Allotment</span>
            </div>
            <div className="elp-stat-card">
              <span className="elp-stat-num" style={{ color: '#0d9488' }}>{totalBalance}</span>
              <span className="elp-stat-desc">Available Balance</span>
            </div>
            <div className="elp-stat-card">
              <span className="elp-stat-num" style={{ color: '#d97706' }}>{totalConsumed}</span>
              <span className="elp-stat-desc">Used Days</span>
            </div>
            <div className="elp-stat-card">
              <span className="elp-stat-num" style={{ color: '#6366f1' }}>{totalPending}</span>
              <span className="elp-stat-desc">Pending Approval</span>
            </div>
          </div>

          {/* Per-Leave Type Balances */}
          <div className="elp-section-block">
            <h3 className="elp-section-title">Leave Allotments & Balances ({year})</h3>
            {loadingBalances ? (
              <p className="lb-ineligible-desc">Loading leave balances...</p>
            ) : snapshots.length === 0 ? (
              <p className="lb-ineligible-desc">No leave policy balances configured for this employee.</p>
            ) : (
              <div className="elp-balance-grid">
                {snapshots.map(s => {
                  /*
                   * totalEntitlement, not bare `granted` — the same correction the summary
                   * tiles above already make, and for the same reason. Comp-Off is earned
                   * rather than granted, so its `granted` is 0: this card read "2 / 0 days"
                   * with a progress bar stuck at empty while the balance beside it said 2.
                   */
                  const entitlement = LeaveBalanceService.totalEntitlement(s);
                  const pct = entitlement > 0 ? Math.min(100, Math.max(0, (s.balance / entitlement) * 100)) : 0;
                  return (
                    <div key={s.leaveTypeCode} className="elp-balance-card">
                      <div className="elp-bal-card-header">
                        <span className="elp-bal-code">{leaveTypeName(s.leaveTypeCode)}</span>
                        <span className="elp-bal-val">
                          <strong>{s.balance}</strong> / {entitlement} days
                        </span>
                      </div>
                      <div className="elp-progress-bar">
                        <div className="elp-progress-fill" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="elp-bal-sub">
                        <span title={usedTooltip(s)}>Used: {LeaveBalanceService.totalUsed(s)}</span>
                        {s.pending > 0 && <span style={{ color: '#d97706' }}>Pending: {s.pending}</span>}
                        {/* HR needs the deadline too: they are the ones fielding "why did my
                            comp-off disappear", and this drawer is where they look it up. */}
                        {expirySummary(s.expiringGrants) && (
                          <span style={{ color: '#b45309' }} title={expiryTooltip(s.expiringGrants)}>
                            ⏳ {expirySummary(s.expiringGrants)}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Leave History & Requests Section */}
          <div className="elp-section-block">
            <div className="elp-history-header">
              <h3 className="elp-section-title">Leave History & Requests</h3>
              <select
                className="la-date-input"
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                style={{ height: 32, fontSize: 12 }}
              >
                {/* Options come from the LeaveStatus enum, which holds the real display word
                    for each state. The hand-written list this replaces labelled
                    LeaveStatus.SUBMITTED as "Pending" — a word no status actually has, so the
                    filter disagreed with the Status column beside it — and omitted
                    "Cancellation Requested" entirely, leaving no way to filter for it. */}
                <option value="ALL">All Statuses</option>
                {Object.values(LeaveStatus).map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            {/* Sub-tabs */}
            <div className="lp-tabs" style={{ marginBottom: 16 }}>
              <button
                type="button"
                className={`lp-tab ${activeTab === 'ALL' ? 'active' : ''}`}
                onClick={() => setActiveTab('ALL')}
              >
                All ({empLeaves.length})
              </button>
              <button
                type="button"
                className={`lp-tab ${activeTab === 'UPCOMING' ? 'active' : ''}`}
                onClick={() => setActiveTab('UPCOMING')}
              >
                Upcoming ({upcomingApproved.length})
              </button>
              <button
                type="button"
                className={`lp-tab ${activeTab === 'PENDING' ? 'active' : ''}`}
                onClick={() => setActiveTab('PENDING')}
              >
                Pending ({pendingRequests.length})
              </button>
              <button
                type="button"
                className={`lp-tab ${activeTab === 'PAST' ? 'active' : ''}`}
                onClick={() => setActiveTab('PAST')}
              >
                Past ({pastHistory.length})
              </button>
            </div>

            {filteredList.length === 0 ? (
              <div className="elp-empty-card" style={{ padding: '24px 0', textAlign: 'center' }}>
                <p className="lb-ineligible-desc" style={{ fontSize: '0.9rem', color: '#64748b' }}>
                  No leave records found for this employee.
                </p>
              </div>
            ) : (
              <div className="la-table-wrapper">
                <table className="la-table">
                  <thead>
                    <tr>
                      <th>Leave Type</th>
                      <th>Dates</th>
                      <th>Days</th>
                      <th>Status</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredList.map(req => (
                      <tr key={req.id}>
                        <td>
                          <span className="la-leave-type-badge">{req.type}</span>
                        </td>
                        <td className="la-dates">
                          <span>{req.startDate === req.endDate ? req.startDate : `${req.startDate} → ${req.endDate}`}</span>
                        </td>
                        <td className="la-days">
                          <strong>{leaveDaysFor(req)}</strong>
                        </td>
                        <td>
                          <span className={`lb-status-badge status-${String(req.status).toLowerCase()}`}>
                            {req.status}
                          </span>
                        </td>
                        <td style={{ maxWidth: 180 }}>
                          <span className="elp-reason-text">{req.reason || '—'}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeLeaveProfileDrawer;
